/**
 * Uma contestada do portão da triagem (E67.F1).
 *
 * O caso tem N contestadas e o portão passou a rodar uma triagem para cada. Este
 * componente é o que existe **por contestada**: resolve o blob da assinatura
 * dela (o hook não pode ser chamado em laço no pai) e mostra um de três estados
 * — a triagem em curso, a falha desta contestada, ou o resultado.
 *
 * Falha isolada de propósito: uma contestada que falhou não impede as outras de
 * aparecerem. Antes da E67 isso não existia como problema porque só a PRIMEIRA
 * contestada era triada.
 */
import { Loader2 } from 'lucide-react';
import { Blueprint } from '@/components/ui/Blueprint';
import { Button } from '@/components/ui/Button';
import { CardKicker } from '@/components/ui/Card';
import { useSignatureBlob } from '@/components/Workspace/useSignatureBlob';
import { ptBR } from '@/i18n/pt-BR';
import type { SignatureRecordData } from '@/lib/case_signatures';
import type { PreAnaliseRecordData } from '@/lib/pre_analise';
import { PreAnaliseResult } from './PreAnaliseResult';

export interface PreAnaliseTriagemItemProps {
  caseId: string;
  contestada: SignatureRecordData;
  /** Padrões do caso — a comparação usou TODOS eles (E67.F1.T03). */
  padroes: SignatureRecordData[];
  preAnalise: PreAnaliseRecordData | null;
  /** Rótulo da contestada na sequência do caso ("Contestada 2 de 5"). */
  ordinal: string;
  erro: string | null;
  rodando: boolean;
  busy: boolean;
  onRetry: () => void;
}

export function PreAnaliseTriagemItem({
  caseId,
  contestada,
  padroes,
  preAnalise,
  ordinal,
  erro,
  rodando,
  busy,
  onRetry,
}: PreAnaliseTriagemItemProps): JSX.Element {
  const contestedSrc = useSignatureBlob(caseId, contestada.id);

  if (preAnalise !== null) {
    return (
      <article data-testid="pre-analise-item" data-contestada={contestada.id}>
        <CardKicker className="block">{ordinal}</CardKicker>
        <PreAnaliseResult
          caseId={caseId}
          preAnalise={preAnalise}
          contestedSrc={contestedSrc}
          padroes={padroes}
        />
      </article>
    );
  }

  if (erro !== null) {
    return (
      <Blueprint
        as="article"
        className="p-sc-6"
        data-testid="pre-analise-item-erro"
        data-contestada={contestada.id}
      >
        <CardKicker className="block">{ordinal}</CardKicker>
        <p className="mt-sc-2 text-body-sm text-danger-700" role="alert">
          {erro}
        </p>
        <Button
          variant="secondary"
          size="sm"
          className="mt-sc-3"
          onClick={onRetry}
          disabled={busy || rodando}
          data-testid="pre-analise-item-retry"
        >
          {rodando ? ptBR.preAnalise.running : ptBR.preAnalise.runCta}
        </Button>
      </Blueprint>
    );
  }

  return (
    <Blueprint
      as="article"
      className="flex items-center gap-sc-3 p-sc-6 text-body-md text-fg-muted"
      role="status"
      aria-live="polite"
      data-testid="pre-analise-item-rodando"
      data-contestada={contestada.id}
    >
      <Loader2 className="h-5 w-5 animate-spin text-accent" aria-hidden />
      <span>
        {ordinal} · {ptBR.preAnalise.running}
      </span>
    </Blueprint>
  );
}
